import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useDropdownPosition } from './useDropdownPosition.js'
import { useClickOutside } from './useClickOutside.js'

/**
 * Composable for managing dropdowns with Teleport
 * Handles positioning, visibility, click outside, and viewport events
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.position - Position: 'top', 'bottom', 'left', 'right', 'auto' (default: 'bottom')
 * @param {number} options.gap - Gap between trigger and dropdown (default: 8)
 * @param {number} options.viewportPadding - Padding from viewport edges (default: 8)
 * @param {string} options.zIndex - Z-index value (default: '50')
 * @param {Function} options.customPosition - Custom positioning function
 * @param {Function} options.onToggle - Callback when dropdown is toggled
 * @param {Array<string>} options.excludeSelectors - CSS selectors to exclude from click outside
 * @returns {Object} Dropdown utilities
 */
export function useTeleportDropdown(options = {}) {
  const {
    position = 'bottom',
    gap = 8,
    viewportPadding = 8,
    zIndex = '50',
    customPosition = null,
    onToggle = null,
    excludeSelectors = []
  } = options

  const isOpen = ref(false)
  const triggerElement = ref(null)
  const dropdownElement = ref(null)

  const { dropdownStyle, calculatePosition } = useDropdownPosition({
    position,
    gap,
    viewportPadding,
    customPosition
  })

  // Calculate position when dropdown opens or element becomes available
  const updatePosition = async () => {
    if (isOpen.value && triggerElement.value && dropdownElement.value) {
      await calculatePosition(triggerElement.value, dropdownElement.value)
    }
  }

  // Handle window resize and scroll
  const handleViewportChange = () => {
    if (isOpen.value) {
      updatePosition()
    }
  }

  // Toggle dropdown
  const toggle = async () => {
    isOpen.value = !isOpen.value
    if (isOpen.value) {
      await updatePosition()
    }
    if (onToggle && typeof onToggle === 'function') {
      onToggle(isOpen.value)
    }
  }

  // Open dropdown
  const open = async () => {
    if (!isOpen.value) {
      isOpen.value = true
      await updatePosition()
      if (onToggle && typeof onToggle === 'function') {
        onToggle(true)
      }
    }
  }

  // Close dropdown
  const close = () => {
    if (isOpen.value) {
      isOpen.value = false
      if (onToggle && typeof onToggle === 'function') {
        onToggle(false)
      }
    }
  }

  // Watch for changes in open state and element availability
  watch([isOpen, dropdownElement], () => {
    if (isOpen.value && dropdownElement.value) {
      nextTick(() => {
        updatePosition()
      })
    }
  })

  // Setup click outside handler
  useClickOutside({
    isOpen,
    onClose: close,
    containerRef: triggerElement,
    dropdownRef: dropdownElement,
    excludeSelectors
  })

  // Setup viewport event listeners
  onMounted(() => {
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleViewportChange)
    window.removeEventListener('scroll', handleViewportChange, true)
  })

  // Computed style with z-index
  const computedStyle = computed(() => ({
    ...dropdownStyle.value,
    zIndex
  }))

  return {
    isOpen,
    triggerElement,
    dropdownElement,
    dropdownStyle: computedStyle,
    toggle,
    open,
    close,
    updatePosition
  }
}

